import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SkipNav from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { AIProtonModel, ProductCardSelections } from "@NA/Widgets/AIProton/AIProtonModel";
import AICarouselProduct from "@NA/Widgets/AIProton/AIProtonProductCard";
import CarouselLeft0 from "@NA/Icons/CarouselLeftInactive";
import CarouselLeft1 from "@NA/Icons/CarouselLeftActive";
import CarouselRight0 from "@NA/Icons/CarouselRightInactive";
import CarouselRight1 from "@NA/Icons/CarouselRightActive";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { productCarouselStyles } from "@NA/Widgets/AIProton/AIProtonStyles";
import { Hidden } from "@insite/mobius/index";
import { css } from "styled-components";

interface OwnProps {
    title: string;
    gaPromoCode: string;
    products: AIProtonModel[];
    realTimePricing?: ProductPriceDto[] | [];
    realTimeInventory?: ProductInventoryDto[] | [];
    titleTextAlignment: any;
}

type Props = ProductCardSelections & OwnProps;

const styles = productCarouselStyles;

const AIProductCarousel: React.FC<Props> = ({
    title,
    products,
    showBrand,
    showPartNumbers,
    showTitle,
    showPrice,
    showImage,
    showSavingsAmount,
    showSavingsPercent,
    gaPromoCode,
    showAvailability,
    titleTextAlignment,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const theme = baseTheme;

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [slideProducts, setSildeProduct] = React.useState<ProductModel[]>();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
        if (embla) {
            setCurrentSlide(embla.selectedScrollSnap());
            setSelectedIndex(embla.selectedScrollSnap());
        }
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, []);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        } else {
            return true;
        }
        //return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        handleArrowButtonsToggle();
    }, [selectedIndex]);

    const handleArrowButtonsToggle = (i = selectedIndex) => {
        if (i === 0) {
            setCanScrollPrev(true);
            setCanScrollNext(true);
            return;
        }
        if (i > 0 && slideProducts?.length && i < slideProducts.length - 1) {
            setCanScrollNext(true);
            setCanScrollPrev(true);
            return;
        }
        if (slideProducts?.length && i === slideProducts?.length - 1) {
            setCanScrollNext(false);
            setCanScrollPrev(true);
            return;
        }
    };

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }
        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            //loop: products.length > slidesToScroll,
            loop: true,
        });
        setCanScroll();
    }, [embla,slidesToScroll, draggable, products]);

    const [currentSlide, setCurrentSlide] = React.useState(0);
    
    if (!products || products.length === 0) {
        return null;
    }
    // slider dots

    const totalSlides = Math.ceil(products.length / slidesToScroll);

    const renderDots = () => {
        const dots = [];
        for (let i = 0; i < totalSlides; i++) {
            dots.push(
                <li key={i}>
                    <button
                        onClick={() => {
                            embla && embla.scrollTo(i);
                            setCurrentSlide(i);
                        }}
                        className={i == currentSlide ? "active" : ""}
                    ></button>
                </li>,
            );
        }
        return dots;
    };

    const prevButton = () => {
        embla && embla?.scrollPrev()
        setCurrentSlide(currentSlide - 1);

    };

    const nextButton = () =>{
        embla && embla?.scrollNext()
        setCurrentSlide(currentSlide + 1);
    };

    const resetDots = () => {
        setCurrentSlide(!canScrollNext ? totalSlides - 1 : 0);
    };
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography as="h4" {...(titleTextAlignment == "center" ? styles.titleTextCenter : styles.titleText)}>{title}</Typography>

            <GridContainer {...styles.mainContainer} data-test-selector={`productCarousel_AIProton`}>
                <Hidden>
                    <GridItem {...styles.prevArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={!canScrollPrev ? resetDots : prevButton}
                                //disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                                className={!canScrollPrev ? "disabled" : ""}
                            >
                                <ButtonIcon src={!canScrollPrev ? CarouselLeft0 : CarouselLeft1} />
                            </Button>
                        )}
                    </GridItem>
                </Hidden>
                <GridItem {...styles.carouselGridItem}>
                <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef} data-ga-promo={gaPromoCode}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {products.map(product => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${product.id}`}
                                            data-test-selector="productContainer"
                                        >
                                            <AICarouselProduct
                                                product={product}
                                                key={product.id}
                                                showBrand={showBrand}
                                                showPartNumbers={showPartNumbers}
                                                showSavingsPercent={showSavingsPercent}
                                                showSavingsAmount={showSavingsAmount}
                                                showImage={showImage}
                                                showTitle={showTitle}
                                                showPrice={showPrice}
                                                showAvailability={showAvailability}
                                                carouselId={""}
                                                title={title}
                                                showItemCompare={true} sourceName={""} updateProductInfo={undefined} productInfo={undefined} />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <Hidden>
                    <GridItem {...styles.nextArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={!canScrollNext ? resetDots :  nextButton}
                                //disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                                className={!canScrollNext ? "disabled" : ""}
                            >
                                <ButtonIcon src={!canScrollNext ? CarouselRight0 : CarouselRight1} />
                            </Button>
                        )}
                    </GridItem>
                </Hidden>
            </GridContainer>
            {products.length > slidesToScroll && (
                <StyledWrapper {...styles.dotsContainerWrapper}>{renderDots()}</StyledWrapper>
            )}
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

export default AIProductCarousel;
